import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from "@mui/material";
import {
    catalogPrimaryButtonSx,
    catalogQuietButtonSx,
} from "./catalogGridStyles.js";

function toDateInputValue(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

function defaultStart() {
    return toDateInputValue(new Date());
}

function defaultEnd() {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return toDateInputValue(d);
}

/**
 * Dialog to schedule an eBay markdown campaign (% off + date range).
 */
export default function EbayCampaignDialog({
    open,
    onClose,
    onConfirm,
    items = [],
    submitting = false,
}) {
    const [discountPercent, setDiscountPercent] = useState("10");
    const [startDate, setStartDate] = useState(defaultStart);
    const [endDate, setEndDate] = useState(defaultEnd);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open) return;
        setDiscountPercent("10");
        setStartDate(defaultStart());
        setEndDate(defaultEnd());
        setError("");
    }, [open]);

    const validationError = useMemo(() => {
        const pct = Number(discountPercent);
        if (!Number.isFinite(pct) || pct < 5 || pct > 80) {
            return "Discount must be between 5% and 80%.";
        }
        if (!startDate || !endDate) {
            return "Start and end dates are required.";
        }
        const startMs = Date.parse(`${startDate}T00:00:00.000Z`);
        const endMs = Date.parse(`${endDate}T23:59:59.000Z`);
        if (!(endMs > startMs)) {
            return "End date must be after start date.";
        }
        if (endMs - startMs < 24 * 60 * 60 * 1000) {
            return "Campaign must run at least 24 hours.";
        }
        if (endMs - startMs > 45 * 24 * 60 * 60 * 1000) {
            return "eBay US campaigns cannot exceed 45 days.";
        }
        return "";
    }, [discountPercent, startDate, endDate]);

    const handleSubmit = () => {
        if (validationError) {
            setError(validationError);
            return;
        }
        setError("");
        onConfirm?.({
            discountPercent: Math.round(Number(discountPercent)),
            startDate,
            endDate,
        });
    };

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 700 }}>
                {items.length === 1
                    ? "Add eBay campaign"
                    : `Add eBay campaign (${items.length})`}
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ fontSize: 14, color: "#5c6570", mb: 1.5 }}>
                    Schedule a percentage markdown on the selected eBay listing
                    {items.length === 1 ? "" : "s"} via the Marketing API.
                </Typography>
                {items.length > 0 ? (
                    <Box
                        component="ul"
                        sx={{ m: 0, mb: 2, pl: 2.5, maxHeight: 140, overflowY: "auto" }}
                    >
                        {items.map((item) => {
                            const key = typeof item === "string" ? item : item.key;
                            const label = typeof item === "string" ? item : item.label;
                            return (
                                <Typography
                                    component="li"
                                    key={key}
                                    sx={{ fontSize: 13, fontWeight: 600 }}
                                >
                                    {label}
                                </Typography>
                            );
                        })}
                    </Box>
                ) : null}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <TextField
                        label="% Discount"
                        size="small"
                        type="number"
                        value={discountPercent}
                        onChange={(e) => setDiscountPercent(e.target.value)}
                        inputProps={{ min: 5, max: 80, step: 1 }}
                        helperText="eBay allows 5–80%"
                        fullWidth
                        disabled={submitting}
                    />
                    <TextField
                        label="Start date"
                        size="small"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        disabled={submitting}
                    />
                    <TextField
                        label="End date"
                        size="small"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        disabled={submitting}
                    />
                </Box>
                {error || validationError ? (
                    <Typography sx={{ mt: 1.5, fontSize: 13, color: "#b42318" }}>
                        {error || validationError}
                    </Typography>
                ) : null}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={submitting} sx={catalogQuietButtonSx}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    disabled={submitting || Boolean(validationError) || !items.length}
                    sx={catalogPrimaryButtonSx}
                    startIcon={
                        submitting ? <CircularProgress size={14} color="inherit" /> : null
                    }
                >
                    Create campaign
                </Button>
            </DialogActions>
        </Dialog>
    );
}
